/**
 * RoleGuard — restringe rotas a perfis específicos.
 *
 * Operador (perfil A) → redireciona pra /dashboard.
 * Sem perfil ainda (loading) → mostra carregando.
 * Sem sessão → ProtectedRoute já redireciona pra /login antes deste guard.
 *
 * Use envolvendo a tela protegida:
 *   <RoleGuard allow={['admin', 'tecnico_responsavel']}>
 *     <HistoricoTrocasPage />
 *   </RoleGuard>
 */

import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, type UserProfile } from '@/hooks/useAuth';
import { eventosApp } from '@/services/datasources/eventos-app';

interface Props {
  allow: UserProfile[];
  children: React.ReactNode;
}

export function RoleGuard({ allow, children }: Props) {
  const { profile, loading, profileLoading } = useAuth();
  const { pathname } = useLocation();
  const carregando = loading || profileLoading;
  const negado = !carregando && (!profile || !allow.includes(profile));
  // Só é "acesso negado" quando o perfil é CONHECIDO e não serve. Perfil nulo
  // é falha de rede ao buscá-lo ou o SIGNED_OUT de quem clicou em "Sair" aqui
  // dentro — registrar isso acusaria um RT legítimo (revisão independente).
  const negadoPorPerfil = negado && profile !== null;

  // GUPM-335: a negação vai para a trilha. Quem tenta abrir /auditoria sem
  // perfil B é exatamente o que um auditor quer saber — antes o redirect era
  // silencioso. Efeito (não render) para registrar uma vez por negação.
  useEffect(() => {
    if (!negadoPorPerfil) return;
    void eventosApp.registrar('acesso_negado', {
      rota: pathname,
      perfil: profile,
      perfis_exigidos: allow,
    });
    // `allow` é literal na rota; re-registrar só quando a negação ou a rota mudam.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negadoPorPerfil, pathname]);

  if (carregando) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <p className="text-sm text-jf-muted">Carregando…</p>
      </div>
    );
  }

  if (negado) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
